import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import client, { setToken, clearToken } from "@/lib/apiClient";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState({ available_balance: 0, reserved_balance: 0 });

  const refreshWallet = useCallback(async () => {
    try {
      const { data } = await client.get("/wallet");
      setWallet(data);
    } catch (_) {}
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await client.get("/auth/me");
      setUser(data);
      await refreshWallet();
    } catch (_) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [refreshWallet]);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithPassword = async (email, password) => {
    const { data } = await client.post("/auth/login", { email, password });
    // Admin MFA gate: either enrollment payload or a code challenge — return so the UI can handle it.
    if (data.mfa_required || data.mfa_setup_required) {
      return { mfa: data };
    }
    setToken(data.session_token);
    setUser(data.user);
    await refreshWallet();
    return { user: data.user };
  };

  const completeMfa = async (payload) => {
    const url = payload.setup ? "/auth/mfa/setup/verify" : "/auth/mfa/verify";
    const { data } = await client.post(url, {
      challenge_id: payload.challenge_id, code: payload.code,
      ...(payload.setup ? {} : { method: payload.method || "totp" }),
    });
    setToken(data.session_token);
    setUser(data.user);
    await refreshWallet();
    return data.user;
  };

  const requestEmailOtp = async (challenge_id) => {
    const { data } = await client.post("/auth/mfa/email-otp", { challenge_id });
    return data;
  };

  const register = async (name, email, password) => {
    const { data } = await client.post("/auth/register", { name, email, password });
    setToken(data.session_token);
    setUser(data.user);
    await refreshWallet();
    return data.user;
  };

  const finishGoogle = async (sessionId) => {
    const { data } = await client.post("/auth/session", {}, { headers: { "X-Session-ID": sessionId } });
    setToken(data.session_token);
    setUser(data.user);
    await refreshWallet();
    return data.user;
  };

  const logout = async () => {
    try { await client.post("/auth/logout"); } catch (_) {}
    clearToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, wallet, refreshWallet, loginWithPassword, completeMfa, requestEmailOtp, register, finishGoogle, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
